import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './Dashboard.css';

export default function DepartmentDashboard({ department }) {
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalSubmissions: 0,
    completionRate: 0,
    submissionsByType: {},
    recentActivity: []
  });

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        console.log('Fetching data for department:', department);
        
        // Get admin location ID from user's assigned locations
        const adminLocationId = '27gQp4BHDD8lHpESiyed'; // Admin location ID
        
        // Convert department name to locationId
        const departmentLocationId = department === 'admin' ? adminLocationId : department;
        console.log('Department location ID:', departmentLocationId);
        
        // First, get all locations to find the matching one
        const locationsRef = collection(db, 'locations');
        const locationsSnap = await getDocs(locationsRef);
        const allLocations = locationsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('All locations:', allLocations);
        
        // Find the location that matches this department
        const departmentLocation = allLocations.find(loc => 
          loc.type?.toLowerCase() === department.replace(/-/g, ' ')
        );
        console.log('Found department location:', departmentLocation);
        
        if (!departmentLocation) {
          console.error('Could not find location for department:', department);
          return;
        }
        
        // Get all templates to see their structure
        const templatesRef = collection(db, 'templates');
        const templatesSnap = await getDocs(templatesRef);
        
        // Get all locations of this department type
        const departmentLocations = allLocations
          .filter(loc => {
            const isMatch = loc.type?.toLowerCase() === department.replace(/-/g, ' ');
            if (department === 'cafe') {
              console.log(`Checking cafe location match:`, {
                locationName: loc.name,
                locationType: loc.type,
                isMatch: isMatch
              });
            }
            return isMatch;
          })
          .map(loc => loc.id);
          
        console.log(`${department} location IDs:`, departmentLocations);
        
        // Get all templates
        const allTemplates = templatesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        if (department === 'cafe') {
          console.log('All templates before filtering:', allTemplates.map(t => ({
            name: t.name,
            status: t.status,
            assignedLocations: t.assignedLocations
          })));
        }
        
        // Filter templates that are assigned to this department and are active or submitted
        const submissions = allTemplates
          .filter(doc => {
            if (department === 'cafe') {
              console.log(`\nChecking template "${doc.name}":`, {
                hasAssignedLocations: !!doc.assignedLocations,
                status: doc.status,
                assignedTo: doc.assignedLocations?.map(l => l.name)
              });
            }
            
            // Must have assignedLocations and be either active or submitted
            if (!doc.assignedLocations || !doc.status) return false;
            if (doc.status !== 'active' && doc.status !== 'submitted') return false;
            
            // Check if template is assigned to any location of this department
            return doc.assignedLocations.some(loc => 
              departmentLocations.includes(loc.id)
            );
          })
          .sort((a, b) => {
            const dateA = a.submittedAt?.toDate() || a.createdAt?.toDate();
            const dateB = b.submittedAt?.toDate() || b.createdAt?.toDate();
            return dateB - dateA;
          })
          .slice(0, 50);

        console.log('Filtered submissions:', submissions);
        console.log('Submission details:', submissions.map(s => ({
          name: s.name,
          status: s.status,
          assignedTo: s.assignedLocations.map(l => l.name)
        })));
        setSubmissions(submissions);

        // Calculate department analytics
        const analytics = {
          totalSubmissions: submissions.length,
          completionRate: calculateCompletionRate(submissions),
          submissionsByType: calculateSubmissionsByType(submissions),
          recentActivity: submissions.slice(0, 10)
        };

        setAnalytics(analytics);
      } catch (error) {
        console.error('Error fetching department data:', error);
      }
    };

    if (department) {
      fetchDepartmentData();
    }
  }, [department]);

  const calculateCompletionRate = (submissions) => {
    // Consider 'submitted' status as completed
    const completed = submissions.filter(s => s.status === 'submitted' || s.status === 'completed').length;
    return submissions.length > 0 ? (completed / submissions.length) * 100 : 0;
  };

  const calculateSubmissionsByType = (submissions) => {
    return submissions.reduce((acc, submission) => {
      const type = submission.type?.name || 'Uncategorized';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  };

  // Convert department ID back to display name
  const departmentDisplayName = department === 'admin' ? 'Admin' : department.split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <div className="department-dashboard">
      <h2>{departmentDisplayName} Dashboard</h2>
      
      <div className="analytics-section">
        <div className="analytics-grid">
          <div className="analytics-card">
            <h3>Total Submissions</h3>
            <p>{analytics.totalSubmissions}</p>
          </div>
          <div className="analytics-card">
            <h3>Completion Rate</h3>
            <p>{analytics.completionRate.toFixed(1)}%</p>
          </div>
        </div>

        <div className="submissions-by-type">
          <h3>Submissions by Type</h3>
          <div className="type-grid">
            {Object.entries(analytics.submissionsByType).map(([type, count]) => (
              <div key={type} className="type-card">
                <h4>{type}</h4>
                <p>{count}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {submissions.map(submission => (
            <div key={submission.id} className="activity-card">
              <h4>{submission.templateName || 'Untitled Form'}</h4>
              <p>Submitted: {new Date(submission.submittedAt?.toDate()).toLocaleString()}</p>
              <p>Status: {submission.status}</p>
              <p>Type: {submission.type?.name || 'Uncategorized'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
